import React, { useMemo, useState } from 'react'
import {
  Box,
  TextField,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Typography,
  CircularProgress,
  Alert,
  Checkbox,
  Chip
} from '@mui/material'
import { Vehicle } from '@otw/models'
import { useInventory } from 'hooks/inventory'
import { PrivateUserInfoType } from '@otw/models'

interface VehicleSelectionTabProps {
  selectedVehicles: string[]
  selectedUser?: PrivateUserInfoType | null
  onVehicleToggle: (vehicleId: string) => void
  onVehiclesSelect: (vehicles: Vehicle[]) => void
}

export const VehicleSelectionTab: React.FC<VehicleSelectionTabProps> = ({
  selectedVehicles,
  selectedUser,
  onVehicleToggle,
  onVehiclesSelect
}) => {
  const [searchTerm, setSearchTerm] = useState('')
  const [showOnlySuggested, setShowOnlySuggested] = useState(true)
  
  // Use existing useInventory hook
  const { vehicles, isLoading, isError, error } = useInventory('master_inventory')

  // Determine whether a vehicle is associated with the selected user's name in the `who` field
  const userName = useMemo(() => {
    if (!selectedUser) return ''
    const first = selectedUser.first_name?.trim() || ''
    const last = selectedUser.last_name?.trim() || ''
    return `${first} ${last}`.trim().toLowerCase()
  }, [selectedUser])

  const allFilteredVehicles = useMemo(() => {
    const term = (searchTerm || '').toLowerCase()
    return (
      vehicles?.filter(vehicle => {
        const matchesSearch =
          term === '' ||
          vehicle.vin.toLowerCase().includes(term) ||
          `${vehicle.year} ${vehicle.make} ${vehicle.model}`.toLowerCase().includes(term) ||
          (vehicle.stock_number || '').toLowerCase().includes(term) ||
          (vehicle.who || '').toLowerCase().includes(term)

        // Only show vehicles that don't already have an assigned user or are available
        const isAvailable = !vehicle.assigned_user?.id

        return matchesSearch && isAvailable
      }) || []
    )
  }, [vehicles, searchTerm])

  // Sort vehicles so that those where vehicle.who matches the selected user's name appear first
  const filteredVehicles = useMemo(() => {
    const withPriority = [...allFilteredVehicles].sort((a, b) => {
      const aMatch = userName && (a.who || '').toLowerCase().includes(userName) ? 1 : 0
      const bMatch = userName && (b.who || '').toLowerCase().includes(userName) ? 1 : 0
      return bMatch - aMatch
    })

    if (showOnlySuggested && userName) {
      const suggested = withPriority.filter(v => (v.who || '').toLowerCase().includes(userName))
      return suggested.length ? suggested : withPriority
    }
    return withPriority
  }, [allFilteredVehicles, userName, showOnlySuggested])

  // Auto-toggle to suggested when there are suggestions; fall back to all when none
  // Triggered when user selection or inventory/search changes
  React.useEffect(() => {
    if (!userName) {
      setShowOnlySuggested(false)
      return
    }
    const anySuggested = allFilteredVehicles.some(v => (v.who || '').toLowerCase().includes(userName))
    setShowOnlySuggested(anySuggested)
  }, [userName, allFilteredVehicles])

  const getSelectedVehicleObjects = () => {
    return filteredVehicles.filter(vehicle => selectedVehicles.includes(vehicle.id))
  }

  const handleVehicleToggle = (vehicle: Vehicle) => {
    onVehicleToggle(vehicle.id)
    // Update the parent with selected vehicle objects
    const updatedSelection = selectedVehicles.includes(vehicle.id)
      ? getSelectedVehicleObjects().filter(v => v.id !== vehicle.id)
      : [...getSelectedVehicleObjects(), vehicle]
    
    onVehiclesSelect(updatedSelection)
  }

  if (isError) {
    return (
      <Alert severity="error">
        Error loading vehicles: {error?.message}
      </Alert>
    )
  }

  return (
    <Box sx={{ height: '400px', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h6" gutterBottom>
          Select Vehicles for Photo Upload
        </Typography>
        {selectedUser && (
          <Typography variant="body2" color="text.secondary">
            Suggesting vehicles for: {selectedUser.first_name} {selectedUser.last_name}
          </Typography>
        )}
        <Box sx={{ mt: 1, display: 'flex', gap: 1, alignItems: 'center' }}>
          <Chip
            label={showOnlySuggested ? 'Showing suggested first' : 'Showing all'}
            color={showOnlySuggested ? 'primary' : 'default'}
            onClick={() => setShowOnlySuggested(prev => !prev)}
            size="small"
          />
        </Box>
        
        {selectedVehicles.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Selected ({selectedVehicles.length}):
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {getSelectedVehicleObjects().map(vehicle => (
                <Chip
                  key={vehicle.id}
                  label={`${vehicle.year} ${vehicle.make} ${vehicle.model}`}
                  onDelete={() => handleVehicleToggle(vehicle)}
                  size="small"
                />
              ))}
            </Box>
          </Box>
        )}
      </Box>
      
      <TextField
        fullWidth
        label="Search by VIN, make, model, year, or stock number"
        variant="outlined"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        sx={{ mb: 2 }}
      />

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <List sx={{ flex: 1, overflow: 'auto' }}>
          {filteredVehicles.map((vehicle) => (
            <ListItem key={vehicle.id} disablePadding>
              <ListItemButton onClick={() => handleVehicleToggle(vehicle)}>
                <ListItemIcon>
                  <Checkbox
                    edge="start"
                    checked={selectedVehicles.includes(vehicle.id)}
                    tabIndex={-1}
                    disableRipple
                  />
                </ListItemIcon>
                <ListItemText
                  primary={`${vehicle.year} ${vehicle.make} ${vehicle.model}`}
                  secondary={
                    <Typography variant="caption" color="text.secondary">
                      VIN: {vehicle.vin} • Stock #: {vehicle.stock_number || 'N/A'}{vehicle.status ? ` • Status: ${vehicle.status}` : ''}
                      {(vehicle.who && userName && vehicle.who.toLowerCase().includes(userName)) ? ' • suggested' : ''}
                    </Typography>
                  }
                />
              </ListItemButton>
            </ListItem>
          ))}
          
          {filteredVehicles.length === 0 && searchTerm && (
            <ListItem>
              <ListItemText primary="No available vehicles found" />
            </ListItem>
          )}
          
          {filteredVehicles.length === 0 && !searchTerm && !isLoading && (
            <ListItem>
              <ListItemText 
                primary="No available vehicles" 
                secondary="All vehicles are already assigned to users"
              />
            </ListItem>
          )}
        </List>
      )}
    </Box>
  )
} 